import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tabs, Modal, Button } from '@ets/design-system';

import { useDispatch, useSelector, useTabsState } from '_common/hooks';

import { closeAndResetModal } from '_common/modals/ModalsSlice';
import { getDocumentObject } from 'Editor/redux/EditorStatusSlice';
import { useGetMetadataListQuery } from 'App/redux/MetadataApi';

import General from './General/General';
import Authoring from './Authoring/Authoring';
import Template from './Template/Template';
import Metadata from './Metadata/Metadata';
import { IntlErrorBoundary } from '_common/components';

const DocumentDetailsModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { data: metadata } = useGetMetadataListQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: result.data ?? {} }),
  });
  const { tab, onTabChange } = useTabsState('documentDetails');

  const object = useSelector(getDocumentObject) as Objekt & {
    events: {
      warnings: ISODate[];
      due: ISODate;
    };
  };
  const isOpen = useSelector((state) => state.modals.open.DocumentDetailsModal);

  const [validations, setValidations] = useState<string>();
  const [editMetadata, setEditMetadata] = useState(false);

  const handleToggleEditMode = (editMode: boolean) => {
    setEditMetadata(editMode);
  };

  const close = () => {
    dispatch(closeAndResetModal('DocumentDetailsModal'));
  };

  const handleEditMetadata = () => {
    setEditMetadata(false);
  };

  return (
    <Modal open={!!isOpen} close={close} width="78rem" testId="document-details">
      <Modal.Header>
        <FormattedMessage id="DOCUMENT_DETAILS" />
      </Modal.Header>
      <Modal.Body>
        <Tabs value={tab || 'general'} onValueChange={onTabChange}>
          <Tabs.List>
            <Tabs.Trigger value="general">
              {intl.formatMessage({ id: 'settings.tabs.general' })}
            </Tabs.Trigger>
            <Tabs.Trigger value="authors">{intl.formatMessage({ id: 'AUTHORING' })}</Tabs.Trigger>
            <Tabs.Trigger value="template">
              {intl.formatMessage({ id: 'global.template' })}
            </Tabs.Trigger>
            {Object.keys(metadata).length > 0 && (
              <Tabs.Trigger value="metadata">{intl.formatMessage({ id: 'METADATA' })}</Tabs.Trigger>
            )}
          </Tabs.List>
          <Tabs.Content value="general">
            <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
              <General objectId={object.id} />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="authors">
            <IntlErrorBoundary key="authors" size="xl" style={{ margin: '5rem 0' }}>
              <Authoring />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="template">
            <IntlErrorBoundary key="template" size="xl" style={{ margin: '5rem 0' }}>
              <Template />
            </IntlErrorBoundary>
          </Tabs.Content>
          {Object.keys(metadata).length > 0 && (
            <Tabs.Content value="metadata">
              <IntlErrorBoundary key="metadata" size="xl" style={{ margin: '5rem 0' }}>
                <Metadata
                  editMetadata={editMetadata}
                  setEditMetadata={handleToggleEditMode}
                  validations={validations}
                  setValidations={setValidations}
                />
              </IntlErrorBoundary>
            </Tabs.Content>
          )}
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="document-details-modal-close">
          <FormattedMessage id="global.cancel" />
        </Button>
        {editMetadata && (
          <Button
            size="m"
            variant="primary"
            onClick={handleEditMetadata}
            disabled={!!validations}
            testId="document-details-modal-save"
          >
            <FormattedMessage id="SAVE_AND_BACK" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentDetailsModal;
